import React, { useEffect, useMemo, useState } from 'react'
import './bpCount.css'
import { Button } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { utils, writeFile } from 'xlsx-js-style'
import swal from 'sweetalert'
import { ButtonList } from 'sweetalert/typings/modules/options/buttons'
import CustomDatePicker from '../../../widgets/datePicker/datePicker'
import AgGridContainer from '../../../componant/gridContainer/gridContainer'
import { SnackbarService } from '../../../services/snackBehaviorSubject/snackBehaviorSubject'
import { calculateIncentive, editBPCount, getCalculatedIncentive, lockIncentiveDateRange } from '../../../api-services/employee-services'

interface DateRangeProps {
  startDate: string,
  endDate: string
}

interface BPCountRowProps {
  employeeId: string,
  employeeName: string,
  date: string,
  totalCount: number,
  incentiveCount: number,
  originalCount: number,
  incentiveAmount: number
}

interface EditedBPCountRow {
  employeeId: string,
  date: string,
  count: number
}

const dateRangeField: { name: keyof DateRangeProps, label: string }[] = [
  { name: 'startDate', label: 'From Date *' },
  { name: 'endDate', label: 'To Date *' }
]

const initialDateRange: DateRangeProps = {
  startDate: '',
  endDate: ''
}

const formatDate = (isoDate: string): string => {
  if (!isoDate) return ''
  const [year, month, day] = isoDate.split('T')[0].split('-')
  return `${day}/${month}/${year}`
}

const formatIncentive = (value: number | undefined | null): string =>
  value !== undefined && value !== null ? `₹ ${value.toLocaleString('en-IN')}` : ''

const BPCount = () => {
  const snackbarService = SnackbarService()
  const [selectedDate, setSelectedDate] = useState<DateRangeProps>(initialDateRange)
  const [lockedDateRange, setLockedDateRange] = useState<DateRangeProps>(initialDateRange)
  const [activeSearchRange, setActiveSearchRange] = useState<DateRangeProps>(initialDateRange)
  const [errors, setErrors] = useState<DateRangeProps>(initialDateRange)
  const [rowData, setRowData] = useState<BPCountRowProps[]>([])
  const [editedRows, setEditedRows] = useState<Record<string, EditedBPCountRow>>({})

  const displayRows = useMemo(() => {
    if (!activeSearchRange.startDate || !activeSearchRange.endDate) {
      return rowData
    }
    return rowData.filter((row) => {
      const rowDate = row.date?.split('T')[0]
      return rowDate >= activeSearchRange.startDate && rowDate <= activeSearchRange.endDate
    })
  }, [rowData, activeSearchRange])

  const pinnedBottomRowData = useMemo(() => {
    if (displayRows.length === 0) {
      return []
    }
    const totalEmployees = new Set(displayRows.map((row) => row.employeeId)).size
    const totalCount = displayRows.reduce((sum, row) => sum + (Number(row.totalCount) || 0), 0)
    const totalIncentive = displayRows.reduce((sum, row) => sum + (Number(row.incentiveAmount) || 0), 0)
    return [{
      employeeName: `Total Employees: ${totalEmployees}`,
      totalCount: totalCount,
      incentiveAmount: totalIncentive
    }]
  }, [displayRows])


  const fetchIncentive = () => {
    getCalculatedIncentive().then((response: any) => {
      if (response?.status === 200) {
        setRowData(response.data.reverse())
      }
    })
  }

  useEffect(() => {
    fetchIncentive()
  }, [])

  const [columnDefs] = useState([
    {
      headerName: 'Date',
      field: 'date',
      minWidth: 130,
      flex: 1,
      resizable: true,
      filter: true,
      valueFormatter: (params: any) => {
        if (!params.value) return '';

        const [date, time] = params.value.split('T');
        const [year, month, day] = date.split('-');

        return `${day}/${month}/${year} ${time?.substring(0, 8)}`;
      }
    },
    {
      headerName: 'User Id',
      field: 'employeeId',
      minWidth: 130,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Name',
      field: 'employeeName',
      minWidth: 160,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Operator Count',
      field: 'totalCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true,
      editable: true,
    },
    {
      headerName: 'Incentive Count',
      field: 'incentiveCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Original Count',
      field: 'originalCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true,
    },
    {
      headerName: 'Incentive Amount',
      field: 'incentiveAmount',
      minWidth: 160,
      flex: 1,
      resizable: true,
      filter: true,
      valueFormatter: (params: any) => {
        if (params.value === undefined || params.value === null) {
          return '';
        }
        return `₹ ${params.value.toLocaleString('en-IN')}`;
      }
    }
  ])

  const onCellValueChanged = (e: any) => {
    setRowData((prev) => prev.map((row) => row.employeeId === e.data.employeeId && row.date === e.data.date ? e.data : row))
    const key = `${e.data.employeeId}_${e.data.date}`
    setEditedRows((prev) => ({
      ...prev,
      [key]: { employeeId: e.data.employeeId, date: e.data.date?.split('T')[0], count: e.data.count }
    }))
  }

  const handleDate = (date: string, name: string) => {
    setSelectedDate((prev) => {
      const updated = { ...prev, [name]: date }
      if (updated.startDate && updated.endDate && new Date(updated.endDate) < new Date(updated.startDate)) {
        updated.endDate = ''
      }
      return updated
    })
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = () => {
    const newErrors: DateRangeProps = {
      startDate: selectedDate.startDate ? '' : 'From Date is required',
      endDate: selectedDate.endDate ? '' : 'To Date is required'
    }
    setErrors(newErrors)
    if (newErrors.startDate || newErrors.endDate) {
      return
    }
    setLockedDateRange(selectedDate)
    lockIncentiveDateRange({ fromDate: selectedDate.startDate, toDate: selectedDate.endDate }).then((response: any) => {
      if (response?.status === 200) {
        snackbarService.next({ data: { open: true, severity: 'success', message: 'Date range locked successfully' } })
      }
    })
  }

  const handleSearchByDate = () => {
    const newErrors: DateRangeProps = {
      startDate: selectedDate.startDate ? '' : 'From Date is required',
      endDate: selectedDate.endDate ? '' : 'To Date is required'
    }
    setErrors(newErrors)
    if (newErrors.startDate || newErrors.endDate) {
      return
    }
    setActiveSearchRange(selectedDate)
  }

  const handleReset = () => {
    setSelectedDate(initialDateRange)
    setLockedDateRange(initialDateRange)
    setActiveSearchRange(initialDateRange)
    setErrors(initialDateRange)
  }

  const handleCalculate = () => {
    const dateRange = lockedDateRange.startDate && lockedDateRange.endDate ? lockedDateRange : selectedDate
    if (!dateRange.startDate || !dateRange.endDate) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Please select calculate date range' } })
      return
    }
    calculateIncentive({ fromDate: dateRange.startDate, toDate: dateRange.endDate }).then((response: any) => {
      if (response?.status === 200) {
        snackbarService.next({ data: { open: true, severity: 'success', message: 'Incentive calculated successfully' } })
        fetchIncentive()
      }
    })
  }

  const handleSaveEditedData = () => {
    const payload = Object.values(editedRows)
    if (payload.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'No changes to save' } })
      return
    }
    swal({
      title: 'Are you sure?',
      text: 'Do you want to save the edited operator count data?',
      icon: 'warning',
      buttons: true as unknown as ButtonList | (string | boolean)[] | undefined,
    }).then((confirmed) => {
      if (!confirmed) {
        return
      }
      editBPCount(payload).then((response: any) => {
        if (response?.status === 200) {
          snackbarService.next({ data: { open: true, severity: 'success', message: 'Data saved successfully' } })
          setEditedRows({})
          fetchIncentive()
        }
      })
    })
  }

  const applyExcelStyling = (ws: any, header: string[], data: any[][]) => {
    header.forEach((_, colIndex) => {
      const cellRef = utils.encode_cell({ r: 0, c: colIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true }
        }
      }
    })

    ws['!cols'] = header.map((headerText, colIndex) => {
      const maxDataLength = data.reduce((max, row) => {
        const value = row[colIndex]
        const length = value !== undefined && value !== null ? String(value).length : 0
        return Math.max(max, length)
      }, headerText.length)
      return { wch: maxDataLength + 2 }
    })
  }

  const handleDownloadExcel = () => {
    if (displayRows.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Data not available' } })
      return
    }
    const header = ['Boarding Pass Count Date', 'User Id', 'Name', 'Operator Count', 'Incentive Count', 'Original Count', 'Incentive Amount']
    const data = displayRows.map((row) => ([
      formatDate(row.date),
      row.employeeId,
      row.employeeName,
      row.totalCount,
      row.incentiveCount,
      row.originalCount,
      formatIncentive(row.incentiveAmount)
    ]))

    const totalEmployees = new Set(displayRows.map((row) => row.employeeId)).size
    const totalOperatorCount = displayRows.reduce((sum, row) => sum + (Number(row.totalCount) || 0), 0)
    const totalIncentiveCount = displayRows.reduce((sum, row) => sum + (Number(row.incentiveCount) || 0), 0)
    const totalIncentiveAmount = displayRows.reduce((sum, row) => sum + (Number(row.incentiveAmount) || 0), 0)
    const grandTotalRow = [
      'Grand Total',
      '',
      `Total Employees: ${totalEmployees}`,
      totalOperatorCount,
      totalIncentiveCount,
      '',
      formatIncentive(totalIncentiveAmount)
    ]

    const ws = utils.aoa_to_sheet([header, ...data, [], grandTotalRow, []])
    applyExcelStyling(ws, header, [...data, grandTotalRow])

    // bold the grand total row (below a blank row after the data)
    const grandTotalRowIndex = data.length + 2
    header.forEach((_, colIndex) => {
      const cellRef = utils.encode_cell({ r: grandTotalRowIndex, c: colIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = { font: { bold: true } }
      }
    })

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Boarding Pass Count')
    writeFile(wb, 'Boarding Pass Count.xlsx')
  }

  const handleDownloadGroupedExcel = () => {
    if (displayRows.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Data not available' } })
      return
    }
    const header = ['User Id', 'Name', 'Boarding Pass Count Date', 'Operator Count', 'Incentive Count', 'Incentive Amount']

    const sortedRows = [...displayRows].sort((a, b) => {
      if (a.employeeId !== b.employeeId) {
        return String(a.employeeId).localeCompare(String(b.employeeId))
      }
      return String(a.employeeName).localeCompare(String(b.employeeName))
    })

    const data: any[][] = []
    const boldRowIndices: number[] = []
    let userCount = 0
    let userIncentiveCount = 0
    let userIncentive = 0
    let grandCount = 0
    let grandIncentiveCount = 0
    let grandIncentive = 0

    const flushSubtotal = () => {
      data.push([]) // blank row before subtotal
      data.push(['', '', 'Subtotal', userCount, userIncentiveCount, formatIncentive(userIncentive)])
      boldRowIndices.push(data.length)
      data.push([]) // blank row after subtotal
      userCount = 0
      userIncentiveCount = 0
      userIncentive = 0
    }

    sortedRows.forEach((row, index) => {
      const prev = sortedRows[index - 1]
      const isNewUser = !prev || prev.employeeId !== row.employeeId
      if (prev && prev.employeeId !== row.employeeId) {
        flushSubtotal()
      }
      data.push([
        isNewUser ? row.employeeId : '',
        row.employeeName,
        formatDate(row.date),
        row.totalCount,
        row.incentiveCount,
        formatIncentive(row.incentiveAmount)
      ])
      userCount += Number(row.totalCount) || 0
      userIncentiveCount += Number(row.incentiveCount) || 0
      userIncentive += Number(row.incentiveAmount) || 0
      grandCount += Number(row.totalCount) || 0
      grandIncentiveCount += Number(row.incentiveCount) || 0
      grandIncentive += Number(row.incentiveAmount) || 0
    })
    flushSubtotal()
    data.push(['', '', 'Grand Total', grandCount, grandIncentiveCount, formatIncentive(grandIncentive)])
    boldRowIndices.push(data.length)

    const ws = utils.aoa_to_sheet([header, ...data])
    applyExcelStyling(ws, header, data)

    boldRowIndices.forEach((rowIndex) => {
      header.forEach((_, colIndex) => {
        const cellRef = utils.encode_cell({ r: rowIndex, c: colIndex })
        if (ws[cellRef]) {
          ws[cellRef].s = { font: { bold: true } }
        }
      })
    })

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Grouped by User Id')
    writeFile(wb, 'Boarding Pass Count - Grouped by User Id.xlsx')
  }

  return (
    <div className='main'>
      <div className='main-container'>
        <div className='heading heading-center-space-between height-40'>
          <h2>Boarding Pass Incentive Calculation</h2>
        </div>
        <div className='cal-40'>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className='bp-count-date-range-container'>
              {
                dateRangeField.map((item, index: number) => (
                  <div key={index} className='bp-count-date-sub-container'>
                    <CustomDatePicker
                      label={item.label}
                      name={item.name}
                      selectedDate={selectedDate}
                      handleDate={handleDate}
                      minDate={item.name === 'endDate' ? selectedDate.startDate : undefined}
                    />
                    {
                      errors[item.name as keyof DateRangeProps] &&
                      <span className='bp-count-error-text'>{errors[item.name as keyof DateRangeProps]}</span>
                    }
                  </div>
                ))
              }
              <div className='bp-count-button-group'>
                <Button
                  variant='contained'
                  className='bp-count-submit-button'
                  onClick={handleSubmit}
                >
                  Lock
                </Button>
                <Button
                  variant='contained'
                  className='bp-count-search-button'
                  onClick={handleSearchByDate}
                >
                  Search by Date
                </Button>
                <Button
                  variant='outlined'
                  className='bp-count-reset-button'
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </div>
            </div>
            <div className='bp-count-admin-table-header'>
              <Button
                variant='contained'
                className='bp-count-calculate-button'
                onClick={handleCalculate}
              >
                Calculate
              </Button>
              <Button
                variant='outlined'
                className='bp-count-download-button'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
              >
                Boarding Pass Count
              </Button>
              <Button
                variant='outlined'
                className='bp-count-download-button'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadGroupedExcel}
              >
                Grouped by User Id
              </Button>
              <Button
                variant='contained'
                className='bp-count-save-button'
                onClick={handleSaveEditedData}
              >
                Save Edited Data
              </Button>
            </div>
            <div className='ag-grid-container' style={{ flex: 1 }}>
              <AgGridContainer
                rowData={displayRows}
                columnDefs={columnDefs}
                editType='fullRow'
                stopEditingWhenCellsLoseFocus={true}
                singleClickEdit={true}
                onCellValueChanged={onCellValueChanged}
                pinnedBottomRowData={pinnedBottomRowData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BPCount
